import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Rating,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Star } from "@mui/icons-material";
import { BadgeCheck } from "lucide-react";

// ==============================================
// COMPONENTES INTERNOS (AUTOCONTENIDOS)
// ==============================================

const EstrellasPuntuacion = ({ puntuacion, centered = false }) => {
  return (
    <Box display="flex" justifyContent={centered ? "center" : "flex-start"}>
      <Rating
        value={puntuacion}
        readOnly
        precision={0.1}
        emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
      />
    </Box>
  );
};

const AvatarProfesional = ({ profesionalInfo }) => {
  const fotoNueva = profesionalInfo?.perfil?.profesional_foto_perfil;
  const nombre = profesionalInfo?.perfil?.profesional_nombre_plataforma || '';
  const inicial = nombre.trim().charAt(0).toUpperCase();

  if (fotoNueva) {
    return (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
        <img
          src={fotoNueva}
          alt={nombre || 'Profesional'}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    );
  }

  // Fallback legacy: caja azul oscuro con inicial
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        backgroundColor: '#1E3A5F',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Quicksand-Bold',
          fontSize: '16px',
          color: '#FFFFFF',
          lineHeight: 1,
          mb: 0,
        }}
      >
        {inicial}
      </Typography>
    </Box>
  );
};

// ==============================================
// COMPONENTE PRINCIPAL DE LA SECCIÓN
// ==============================================

function SeccionEvaluaciones({ profesionalInfo, currentPage, setCurrentPage }) {
  const theme = useTheme();
  const isMobileEvaluaciones = useMediaQuery(theme.breakpoints.down("sm"));

  // Función para renderizar evaluaciones
  const renderEvaluaciones = (evaluaciones) => {
    if (!evaluaciones || !evaluaciones.comentarios) return null;

    const nombreParts = profesionalInfo?.perfil?.profesional_nombre_plataforma?.split(' ') || [];
    const primerNombreRaw = nombreParts.length > 0 ? nombreParts[0] : 'profesional';
    const primerNombreCapitalizado = primerNombreRaw.charAt(0).toUpperCase() + primerNombreRaw.slice(1).toLowerCase();

    // Elementos por página
    const itemsPerPage = 5;

    // Calcular total de páginas
    const totalPages = Math.ceil(evaluaciones.comentarios.length / itemsPerPage);

    // Obtener elementos a mostrar en la página actual
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentComentarios = evaluaciones.comentarios.slice(
      indexOfFirstItem,
      indexOfLastItem
    );

    // Manejar cambio de página
    const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setCurrentPage(newPage);

        // Scroll a la sección de evaluaciones al cambiar de página
        const evaluacionesSection = document.getElementById(
          "evaluaciones-section"
        );
        if (evaluacionesSection) {
          evaluacionesSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    };

    // Generar números de página con MÁXIMO 3 números visibles
    const getPageNumbers = () => {
      const pages = [];
      
      // Si hay 3 páginas o menos, mostrar todas
      if (totalPages <= 3) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
        return pages;
      }
      
      // Para más de 3 páginas, siempre mostrar máximo 3 números
      if (currentPage === 1) {
        // Página 1: [1, 2, ..., última]
        pages.push(1, 2);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage === 2) {
        // Página 2: [1, 2, ..., última]
        pages.push(1, 2);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage === totalPages) {
        // Última página: [1, ..., penúltima, última]
        pages.push(1);
        pages.push('...');
        pages.push(totalPages - 1, totalPages);
      } else if (currentPage === totalPages - 1) {
        // Penúltima página: [1, ..., penúltima, última]
        pages.push(1);
        pages.push('...');
        pages.push(totalPages - 1, totalPages);
      } else {
        // Páginas del medio: [1, ..., actual, ..., última]
        pages.push(1);
        pages.push('...');
        pages.push(currentPage);
        pages.push('...');
        pages.push(totalPages);
      }
      
      return pages;
    };

    // Color azulado para la paginación
    const colorAzulado = "#4285F4";

    return (
      <Box id="evaluaciones-section" sx={{ mt: 4 }}>
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "20px", xs: "18px" },
            mb: 3,
          }}
        >
          Evaluaciones
        </Typography>

        {/* Promedio general - Diseño original */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: "48px",
              lineHeight: 1,
              color: "#000000",
              mb: 0,
            }}
          >
            {parseFloat(evaluaciones.promedio).toFixed(1)}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 0 }}>
            <EstrellasPuntuacion
              puntuacion={parseFloat(evaluaciones.promedio)}
              centered={false}
            />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "14px", xs: "12px" },
                color: "#666666",
                mb: 0,
              }}
            >
              Basado en {evaluaciones.cantidad} evaluaciones
            </Typography>
          </Box>
        </Box>

        {/* Lista de comentarios - Usando el diseño original de las cards */}
        {currentComentarios.map((comentario, index) => (
          <Card
            key={index}
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <EstrellasPuntuacion puntuacion={parseFloat(comentario.nota)} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#F0F4FF",
                    padding: "4px 10px",
                    borderRadius: "14px",
                    gap: 0.5,
                    '& svg': {
                      color: '#4285F4',
                      fill: '#4285F4',
                    },
                    '& svg path:last-of-type': {
                      stroke: '#FFFFFF',
                      strokeWidth: 2,
                    }
                  }}
                >
                  <BadgeCheck size={isMobileEvaluaciones ? 18 : 22} />
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Bold",
                      fontSize: { md: "15px", xs: "13px" },
                      color: "#4285F4",
                      lineHeight: 1.2,
                      mt: '1px'
                    }}
                  >
                    Cita verificada
                  </Typography>
                </Box>
              </Box>
              {comentario.comentario && (
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "16px", xs: "14px" },
                    mt: 1,
                    mb: 0,
                  }}
                >
                  {comentario.comentario}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "14px", xs: "12px" },
                    color: "text.secondary",
                    mb: 0,
                  }}
                >
                  {comentario.nombre}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "14px", xs: "12px" },
                    color: "text.secondary",
                    mb: 0,
                  }}
                >
                  {comentario.tiempo_relativo}
                </Typography>
              </Box>

              {/* Burbuja de respuesta del profesional */}
              {comentario.respuesta_profesional && (
                <Box
                  sx={{
                    backgroundColor: '#EFF6FF',
                    borderRadius: '8px',
                    p: 1.5,
                    mt: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <AvatarProfesional profesionalInfo={profesionalInfo} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Quicksand-Bold',
                            fontSize: { md: '14px', xs: '13px' },
                            color: '#1E3A8A',
                            mb: 0,
                          }}
                        >
                          Respuesta de {primerNombreCapitalizado}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Quicksand-Regular',
                            fontSize: { md: '12px', xs: '11px' },
                            color: '#6B7280',
                            mb: 0,
                          }}
                        >
                          {comentario.respuesta_profesional.tiempo_relativo}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Quicksand-Regular',
                          fontSize: { md: '13px', xs: '12px' },
                          color: '#1E3A8A',
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          mb: 0,
                        }}
                      >
                        {comentario.respuesta_profesional.comentario}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Paginación personalizada - Solo se muestra si hay más de 3 evaluaciones */}
        {evaluaciones.comentarios.length > itemsPerPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
              mb: 2,
              gap: { xs: 1, md: 1.5 }, // Aumentar espacio en móvil para mejor visualización
              flexWrap: "nowrap", // Evitar saltos de línea
              width: "100%",
              overflow: "visible", // Permitir que se vea todo el contenido
            }}
          >
            {/* Botón Anterior */}
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              sx={{
                minWidth: { xs: "60px", md: "auto" },
                padding: { xs: "6px 8px", md: "6px 8px" },
                color: colorAzulado,
                fontSize: { xs: "14px", md: "16px" },
                textTransform: "none",
                fontFamily: "Quicksand-Bold",
                whiteSpace: "nowrap",
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              Anterior
            </Button>

            {/* Números de página */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 1 },
                flexShrink: 0, // No permitir que se comprima
                mb: 0,
              }}
            >
              {getPageNumbers().map((pageNum, index) =>
                pageNum === "..." ? (
                  <Typography
                    key={`ellipsis-${index}`}
                    sx={{
                      color: "rgba(0, 0, 0, 0.6)",
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: 20, md: 20 },
                      mb: 0,
                    }}
                  >
                    {pageNum}
                  </Typography>
                ) : (
                  <Box
                    key={`page-${pageNum}`}
                    onClick={() => handlePageChange(pageNum)}
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      backgroundColor:
                        currentPage === pageNum ? colorAzulado : "transparent",
                      color: currentPage === pageNum ? "white" : "inherit",
                      cursor: "pointer",
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor:
                          currentPage === pageNum
                            ? colorAzulado
                            : "rgba(66, 133, 244, 0.08)",
                      },
                    }}
                  >
                    {pageNum}
                  </Box>
                )
              )}
            </Box>

            {/* Botón Siguiente */}
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              sx={{
                minWidth: { xs: "70px", md: "auto" },
                padding: { xs: "6px 8px", md: "6px 8px" },
                color: colorAzulado,
                fontSize: { xs: "14px", md: "16px" },
                textTransform: "none",
                fontFamily: "Quicksand-Bold",
                whiteSpace: "nowrap",
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Renderizado condicional de la sección completa
  if (!profesionalInfo || !profesionalInfo.evaluaciones) {
    return null;
  }

  return renderEvaluaciones(profesionalInfo.evaluaciones);
}

export default SeccionEvaluaciones;
